import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	filter,
	map,
	of,
	switchMap,
	tap,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { RankingRequester } from '../../requesters/ranking/ranking.requester';
import {
	IRankingActionModalData,
	RankingAction,
	RankingActionPopupComponent,
} from '../components/ranking-action-popup/ranking-action-popup.component';
import { RankingAdapter } from '../ranking.adapter';
import {
	deleteRanking,
	deleteRankingFail,
	duplicateRanking,
	duplicateRankingFail,
	fetchRankingListFail,
	fetchRankingListSuccess,
	initRankingList,
	navigateToRanking,
	navigateToRankingCreate,
	openDeleteConfirmationPopup,
	openDuplicateConfirmationPopup,
	refreshRankingList,
	toggleRankingStatus,
} from './ranking.actions';

@Injectable()
export class RankingEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToRankingCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToRankingCreate),
				tap(() => this.stateService.go(states.RankingCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToRanking = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToRanking),
				tap((a) =>
					this.stateService.go(states.RankingEdit, {
						rankingId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initRankingList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initRankingList),
			switchMap(() => this.rankingRequester.getRankingList(true)),
			map((response) =>
				fetchRankingListSuccess({
					rankingList: this.rankingAdapter.generateRankingList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchRankingListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshRankingList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshRankingList),
			switchMap(() =>
				this.rankingRequester.getRankingList(false)
			),
			map((response) =>
				fetchRankingListSuccess({
					rankingList: this.rankingAdapter.generateRankingList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchRankingListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteRankings$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteRanking),
			switchMap((action) =>
				this.rankingRequester.deleteRanking(action.rankingId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@ranking.delete.success:`
				);
				return refreshRankingList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@ranking.delete.error:`
				);
				this.store.dispatch(deleteRankingFail());
				return caught;
			})
		)
	);

	public duplicateRankings$ = createEffect(() =>
		this.action$.pipe(
			ofType(duplicateRanking),
			switchMap((action) =>
				this.rankingRequester.duplicateRanking(action.rankingId)
			),
			map(() => refreshRankingList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(duplicateRankingFail());
				return caught;
			})
		)
	);

	public toggleRankingStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleRankingStatus),
			switchMap((action) =>
				action.isActive
					? this.rankingRequester.enableRanking(action.rankingId)
					: this.rankingRequester.disableRanking(action.rankingId)
			),
			map(() => refreshRankingList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshRankingList());
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openDeleteConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openDeleteConfirmationPopup),
				switchMap((action) => {
					const modalRef = this.modalService.openPopup<IRankingActionModalData>(
						RankingActionPopupComponent,
						{
							title: $localize`:i18n=@@ranking.action.popup.delete.header:`,
							subTitle: '',
							rankingId: action.rankingId,
							element: action.rankingLabel,
							action: RankingAction.Delete,
						},
						{
							disableClose: true,
						}
					);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				map(([action]) => {
					action.rankingId.map((id) =>
						this.store.dispatch(
							deleteRanking({
								rankingId: id,
							})
						)
					);
				})
			),
		{
			dispatch: false,
		}
	);

	public openDuplicateConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openDuplicateConfirmationPopup),
				switchMap((action) => {
					const modalRef = this.modalService.openPopup<IRankingActionModalData>(
						RankingActionPopupComponent,
						{
							title: $localize`:i18n=@@ranking.action.popup.duplicate.header:`,
							subTitle: '',
							rankingId: action.rankingId,
							element: action.rankingLabel,
							action: RankingAction.Duplicate,
						},
						{
							disableClose: true,
						}
					);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				map(([action]) => {
					action.rankingId.map((id) =>
						this.store.dispatch(
							duplicateRanking({
								rankingId: id,
							})
						)
					);
				})
			),
		{
			dispatch: false,
		}
	);

	public constructor(
		private readonly action$: Actions,
		private readonly rankingRequester: RankingRequester,
		private readonly rankingAdapter: RankingAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
